import React, { useState, useEffect } from "react";
import {
  CircleChevronLeft,
  Edit3,
  CheckCircle2,
  Calendar,
  Building,
  User,
  Shield,
  FileText,
  Phone,
  Mail,
  Briefcase,
  Banknote,
  FileCheck2,
  Lock,
} from "lucide-react";
import toast from "react-hot-toast";
import { StaffDetails, StaffOption, StaffDepartmentLabels } from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";
import { StaffStatusBadge } from "./StaffStatusBadge";
import { ExitConfirmationModal } from "../../shared/ExitConfirmationModal";
import { StaffDocumentUploadModal } from "../../shared/StaffModals/StaffDocumentUploadModal";
import { StaffSalaryRevisionModal } from "../../shared/StaffModals/StaffSalaryRevisionModal";
import { PersonalTab } from "./DetailsTabs/PersonalTab";
import { EmploymentTab } from "./DetailsTabs/EmploymentTab";
import { SalaryTab } from "./DetailsTabs/SalaryTab";
import { DocumentsTab } from "./DetailsTabs/DocumentsTab";
import defaultAvatar from "../../assets/images/default_avatar.jpg";

interface StaffDetailsComponentProps {
  staffId: string | null;
  readOnly?: boolean;
  isUpdate?: boolean;
  onClose: () => void;
}

export const StaffDetailsComponent: React.FC<StaffDetailsComponentProps> = ({
  staffId,
  readOnly = false,
  isUpdate = false,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<
    "personal" | "employment" | "salary" | "documents"
  >("personal");
  const [details, setDetails] = useState<StaffDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(isUpdate);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [managerOptions, setManagerOptions] = useState<StaffOption[]>([]);

  // Nested modal triggers
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [showSalaryRevModal, setShowSalaryRevModal] = useState(false);

  useEffect(() => {
    if (staffId) {
      fetchStaffDetails();
    }
  }, [staffId]);

  useEffect(() => {
    setIsEditing(isUpdate);
  }, [isUpdate]);

  const fetchStaffDetails = async () => {
    if (!staffId) return;
    try {
      setIsLoading(true);
      const res = await ClinicStaffService.getStaffDetails(staffId);
      if (res.success && res.data) {
        setDetails(res.data);
        setEditFormData(res.data);
        if (!readOnly) {
          ClinicStaffService.getStaffOptions(res.data.clinicId).then((optRes) => {
            if (optRes.success && optRes.data) setManagerOptions(optRes.data);
          });
        }
      } else {
        toast.error(res.message || "Failed to load staff details");
      }
    } catch (error) {
      console.error("Load staff details error:", error);
      toast.error("Error loading staff details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const isFormDirty = () => {
    if (!isEditing || !details) return false;
    return JSON.stringify(editFormData) !== JSON.stringify(details) || Boolean(profilePhotoFile);
  };

  const handleRequestClose = () => {
    if (isFormDirty()) {
      setShowExitConfirm(true);
    } else {
      onClose();
    }
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffId) return;

    try {
      setIsSaving(true);
      const payload = new FormData();
      Object.entries(editFormData).forEach(([key, val]) => {
        if (
          key !== "documents" &&
          key !== "salaryRevisions" &&
          val !== undefined &&
          val !== null
        ) {
          payload.append(key, val.toString());
        }
      });
      if (profilePhotoFile) {
        payload.append("profilePhoto", profilePhotoFile);
      }

      const res = await ClinicStaffService.updateStaffDetails(staffId, payload);
      if (res.success) {
        toast.success(res.message || "Staff details updated successfully");
        setIsEditing(false);
        fetchStaffDetails();
      } else {
        toast.error(res.message || "Failed to update staff details");
      }
    } catch (error) {
      console.error("Update staff error:", error);
      toast.error("Error updating staff details");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const res = await ClinicStaffService.deleteStaffDocument(docId);
      if (res.success) {
        toast.success("Document deleted successfully");
        fetchStaffDetails();
      } else {
        toast.error(res.message || "Failed to delete document");
      }
    } catch (error) {
      console.error("Delete doc error:", error);
      toast.error("Error deleting document");
    }
  };

  if (isLoading) {
    return (
      <div className="p-12 text-center bg-white rounded-2xl border border-gray-200 min-h-[400px] flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm font-medium text-gray-500">Loading staff details...</span>
      </div>
    );
  }

  return (
    <div className="w-full bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-6">
      {/* Top Header Row with Navigation & Mode Toggle */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-4">
        <button
          type="button"
          onClick={handleRequestClose}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-emerald-700 transition-colors group"
        >
          <CircleChevronLeft className="w-6 h-6 text-gray-400 group-hover:text-emerald-600 transition-colors" />
          <span>Back to Staff Listing</span>
        </button>

        {!readOnly && (
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl border transition-colors shadow-2xs ${
              isEditing
                ? "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200"
                : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200"
            }`}
          >
            <Edit3 className="w-4 h-4" />
            <span>{isEditing ? "Cancel Edit" : "Edit Staff Details"}</span>
          </button>
        )}
      </div>

      {/* Staff Profile Hero Card */}
      <div className="p-5 bg-gradient-to-r from-emerald-50/40 via-slate-50 to-white rounded-2xl border border-emerald-100/80 shadow-2xs">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-emerald-600/30 shadow-sm bg-gray-100 flex-shrink-0">
              <img
                src={details?.profilePhoto || defaultAvatar}
                alt={details?.fullName || "Staff"}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl font-bold text-gray-900">
                  {details?.fullName || "Staff Member"}
                </h1>
                {details?.employeeStatus && (
                  <StaffStatusBadge status={details.employeeStatus} />
                )}
                {details?.employeeCode && (
                  <span className="px-2 py-0.5 rounded text-[11px] font-mono font-medium bg-gray-100 text-gray-600 border border-gray-200">
                    {details.employeeCode}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-600 mt-1.5 flex-wrap">
                <span className="font-semibold text-emerald-800">
                  {details?.designation || "Staff"}
                </span>
                <span className="text-gray-300">•</span>
                <span className="text-gray-600">
                  {StaffDepartmentLabels[details?.department as any] || details?.department || "Department"}
                </span>
                <span className="text-gray-300">•</span>
                <span className="font-mono text-gray-600 flex items-center gap-1">
                  <Phone className="w-3 h-3 text-gray-400" />
                  {details?.mobileNumber || "No Phone"}
                </span>
                {details?.username && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="font-mono text-gray-500">@{details.username}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Navigation Tabs with Icons */}
      <div className="flex border-b border-gray-200 overflow-x-auto gap-1">
        {[
          { id: "personal", label: "Personal & Contact", icon: User },
          { id: "employment", label: "Employment & Hierarchy", icon: Briefcase },
          { id: "salary", label: "Salary & Bank Details", icon: Banknote },
          {
            id: "documents",
            label: `Verification & Documents (${details?.documents?.length || 0})`,
            icon: FileCheck2,
          },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 py-3 px-4 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${
                isActive
                  ? "border-emerald-600 text-emerald-700 bg-emerald-50/40 rounded-t-lg"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-emerald-600" : "text-gray-400"}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Body */}
      <div className="pt-2">
        {isEditing ? (
          <form onSubmit={handleSaveEdit} className="space-y-6">
            {activeTab === "personal" && (
              <PersonalTab
                details={details}
                isEditing={true}
                editFormData={editFormData}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "employment" && (
              <EmploymentTab
                details={details}
                isEditing={true}
                editFormData={editFormData}
                managerOptions={managerOptions}
                staffId={staffId || ""}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "salary" && (
              <SalaryTab
                details={details}
                isEditing={true}
                editFormData={editFormData}
                readOnly={readOnly}
                onOpenSalaryRevisions={() => setShowSalaryRevModal(true)}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "documents" && (
              <DocumentsTab
                details={details}
                isEditing={true}
                editFormData={editFormData}
                readOnly={readOnly}
                onOpenDocUpload={() => setShowDocUploadModal(true)}
                onDeleteDoc={handleDeleteDoc}
                onChange={handleFieldChange}
              />
            )}

            {/* Bottom Form Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-5 py-2.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2.5 text-xs font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving Changes...</span>
                  </>
                ) : (
                  <span>Save Changes</span>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {activeTab === "personal" && (
              <PersonalTab
                details={details}
                isEditing={false}
                editFormData={editFormData}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "employment" && (
              <EmploymentTab
                details={details}
                isEditing={false}
                editFormData={editFormData}
                managerOptions={managerOptions}
                staffId={staffId || ""}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "salary" && (
              <SalaryTab
                details={details}
                isEditing={false}
                editFormData={editFormData}
                readOnly={readOnly}
                onOpenSalaryRevisions={() => setShowSalaryRevModal(true)}
                onChange={handleFieldChange}
              />
            )}
            {activeTab === "documents" && (
              <DocumentsTab
                details={details}
                isEditing={false}
                editFormData={editFormData}
                readOnly={readOnly}
                onOpenDocUpload={() => setShowDocUploadModal(true)}
                onDeleteDoc={handleDeleteDoc}
                onChange={handleFieldChange}
              />
            )}
          </div>
        )}
      </div>

      {/* Exit Confirmation Modal */}
      <ExitConfirmationModal
        isOpen={showExitConfirm}
        onCancel={() => setShowExitConfirm(false)}
        onConfirm={() => {
          setShowExitConfirm(false);
          setIsEditing(false);
          onClose();
        }}
      />

      {/* Nested Document Upload Modal */}
      <StaffDocumentUploadModal
        isOpen={showDocUploadModal}
        staffId={staffId || ""}
        staffName={details?.fullName}
        onClose={() => setShowDocUploadModal(false)}
        onSuccess={() => fetchStaffDetails()}
      />

      {/* Nested Salary Revision Modal */}
      <StaffSalaryRevisionModal
        isOpen={showSalaryRevModal}
        staffId={staffId || ""}
        staffName={details?.fullName || ""}
        currentSalary={details?.monthlySalary || 0}
        onClose={() => setShowSalaryRevModal(false)}
        onSuccess={() => fetchStaffDetails()}
      />
    </div>
  );
};
